import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { api } from '../services/api';
import './KnowledgePage.css';

const CodeEditor = lazy(() => import('../components/CodeEditor'));

interface KnowledgeFile {
  name: string;
  path: string;
}

interface FileVersion {
  key: string;
  archivedAt?: string;
  size: number;
}

const CATEGORY_LABELS: Record<string, string> = {
  shared: 'Shared Context',
  prompts: 'Stage Prompts',
  voice: 'Voice Documents',
  brand: 'Brand Documents',
};

const CATEGORY_ORDER: string[] = ['shared', 'prompts', 'voice', 'brand'];

export default function KnowledgePage() {
  const [files, setFiles] = useState<Record<string, KnowledgeFile[]>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Viewer state (read only since 2026-10-22: edits here never reached the
  // canonical files on the Mac, so the next prompt publish overwrote them)
  const [activeFile, setActiveFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string>('');

  // Versions
  const [versions, setVersions] = useState<FileVersion[]>([]);
  const [showVersions, setShowVersions] = useState<boolean>(false);

  const fetchFiles = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const data = await api.get('/api/knowledge/files') as { grouped?: Record<string, KnowledgeFile[]> };
      setFiles(data.grouped || {});
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const openFile = async (filePath: string): Promise<void> => {
    try {
      const data = await api.get(`/api/knowledge/file?path=${encodeURIComponent(filePath)}`) as { content: string };
      setActiveFile(filePath);
      setFileContent(data.content);
      setShowVersions(false);
    } catch (err) {
      setError(`Failed to open ${filePath}: ${(err as Error).message}`);
    }
  };

  const loadVersions = async (): Promise<void> => {
    if (!activeFile) return;
    try {
      const data = await api.get(`/api/knowledge/versions?path=${encodeURIComponent(activeFile)}`) as { versions?: FileVersion[] };
      setVersions(data.versions || []);
      setShowVersions(true);
    } catch (err) {
      setError(`Failed to load versions: ${(err as Error).message}`);
    }
  };

  return (
    <div className="knowledge-page">
      {/* File browser sidebar */}
      <div className="knowledge-sidebar">
        <div className="knowledge-sidebar-header">
          <h2 className="knowledge-sidebar-title">Knowledge Layer</h2>
          <button className="knowledge-refresh" onClick={fetchFiles} title="Refresh">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 7a6 6 0 0111.2-3M13 7a6 6 0 01-11.2 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {loading && <div className="knowledge-loading">Loading files...</div>}
        {error && <div className="knowledge-error">{error}</div>}

        <div className="knowledge-file-tree">
          {CATEGORY_ORDER.map((cat) => {
            const catFiles = files[cat];
            if (!catFiles || catFiles.length === 0) return null;

            return (
              <div key={cat} className="knowledge-category">
                <div className="category-header">
                  {CATEGORY_LABELS[cat] || cat}
                  <span className="category-count">{catFiles.length}</span>
                </div>
                {catFiles.map((file) => (
                  <button
                    key={file.path}
                    className={`knowledge-file ${activeFile === file.path ? 'active' : ''}`}
                    onClick={() => openFile(file.path)}
                    title={file.path}
                  >
                    <span className="file-name">{file.name}</span>
                    {file.path.includes('/') && (
                      <span className="file-subfolder">
                        {file.path.split('/').slice(1, -1).join('/')}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            );
          })}

          {!loading && Object.keys(files).length === 0 && (
            <div className="knowledge-empty">
              No files in R2. Upload prompts to the waw-content-engine bucket.
            </div>
          )}
        </div>
      </div>

      {/* Editor area */}
      <div className="knowledge-editor-area">
        {!activeFile ? (
          <div className="knowledge-editor-empty">
            <p>Select a file to view</p>
          </div>
        ) : (
          <>
            <div className="editor-toolbar">
              <span className="editor-filepath">{activeFile}</span>
              <div className="editor-actions">
                <span className="knowledge-readonly-note">
                  Read only. Prompt changes go through a Code session so the Mac files and the live copies stay in step.
                </span>
                <button
                  className="editor-btn"
                  onClick={loadVersions}
                  title="Version history"
                >
                  History
                </button>
              </div>
            </div>

            <div className="editor-body">
              <Suspense fallback={<div className="knowledge-loading">Loading editor...</div>}>
                <CodeEditor value={fileContent} readOnly />
              </Suspense>
            </div>

            {/* Version history panel */}
            {showVersions && (
              <div className="versions-panel">
                <div className="versions-header">
                  <span>Version History</span>
                  <button className="versions-close" onClick={() => setShowVersions(false)}>Close</button>
                </div>
                {versions.length === 0 ? (
                  <p className="versions-empty">No previous versions</p>
                ) : (
                  <div className="versions-list">
                    {versions.map((v) => (
                      <div key={v.key} className="version-item">
                        <span className="version-date">
                          {v.archivedAt ? new Date(v.archivedAt).toLocaleString() : 'Unknown date'}
                        </span>
                        <span className="version-size">{Math.round(v.size / 1024)}KB</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
